import { useEffect, useState } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import { RouteErrorBoundary } from '@/components/error/RouteErrorBoundary'
import { RequireCalling } from '@/features/calling/components/RequireCalling'
import { FormDesigner } from '@/features/form/components/FormDesigner'
import { AppShell } from '@/features/layout/components/AppShell'
import { NotificationsPage } from '@/features/notification'
import { RequirePermission } from '@/hooks/usePermission'
import { AcceptInvitePage } from '@/routes/AcceptInvitePage'
import { AccountPage } from '@/routes/AccountPage'
import { AuthLayout } from '@/routes/AuthLayout'
import { ConfirmPage } from '@/routes/ConfirmPage'
import { DashboardPage } from '@/routes/DashboardPage'
import { DeveloperLayout } from '@/routes/DeveloperLayout'
import { LoginPage } from '@/routes/LoginPage'
import { ForgotPasswordPage, ResetPasswordPage } from '@/routes/PasswordRecoveryPage'
import { PublicFormPage } from '@/routes/PublicFormPage'
import { RegisterPage } from '@/routes/RegisterPage'
import { RegisterWorkspacePage } from '@/routes/RegisterWorkspacePage'
import TasksPage from '@/routes/TasksPage'
import { WorkspaceSelectPage } from '@/routes/WorkspaceSelectPage'
import { AutomationDetailPage } from '@/routes/automations/AutomationDetailPage'
import { AutomationsPage } from '@/routes/automations/AutomationsPage'
import { CallLogPage } from '@/routes/calling/CallLogPage'
import { MyPhonePage } from '@/routes/calling/MyPhonePage'
import { TelephonySettingsPage } from '@/routes/calling/TelephonySettingsPage'
import { DeveloperAnalyticsPage } from '@/routes/developer/DeveloperAnalyticsPage'
import { DeveloperDashboardPage } from '@/routes/developer/DeveloperDashboardPage'
import { DeveloperUsersPage } from '@/routes/developer/DeveloperUsersPage'
import { DeveloperWorkspacesPage } from '@/routes/developer/DeveloperWorkspacesPage'
import { WorkspaceDetailPage } from '@/routes/developer/WorkspaceDetailPage'
import { WorkspaceSubscriptionPage } from '@/routes/developer/WorkspaceSubscriptionPage'
import { NewRecordGroupPage } from '@/routes/record-groups/NewRecordGroupPage'
import { RecordDetailPage } from '@/routes/record-groups/RecordDetailPage'
import { RecordGroupPage } from '@/routes/record-groups/RecordGroupPage'
import { RecordGroupRoute } from '@/routes/record-groups/RecordGroupRoute'
import { RecordGroupsPage } from '@/routes/record-groups/RecordGroupsPage'
import { RoleDetailPage } from '@/routes/roles/RoleDetailPage'
import { MailboxCallbackPage } from '@/routes/settings/MailboxCallbackPage'
import { WorkspaceSettingsPage } from '@/routes/settings/WorkspaceSettingsPage'
import { UserDetailPage } from '@/routes/users/UserDetailPage'
import { useAuthStore } from '@/stores/auth.store'
import { PERMISSION_KEYS } from '@/stores/permission.store'

function useSessionResolver() {
  const { accessToken, authStep, isLoading, setLoading } = useAuthStore()
  const [isChecking, setIsChecking] = useState(isLoading && !accessToken && authStep !== 'workspace')

  useEffect(() => {
    if (!isLoading || accessToken || authStep === 'workspace') {
      setIsChecking(false)
      return
    }

    let isMounted = true
    // Restores the token and identity into the stores on success.
    import('@/services/api/auth.service')
      .then(({ refreshToken }) => refreshToken())
      .catch(() => {
        // Unauthenticated guest
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false)
          setIsChecking(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [isLoading, accessToken, authStep, setLoading])

  const isAuthenticated = !!accessToken || authStep === 'workspace'
  const redirectTarget = authStep === 'workspace' ? '/workspace-select' : '/'

  return { isChecking, isAuthenticated, redirectTarget }
}

function PublicLoginRoute() {
  const { isChecking, isAuthenticated, redirectTarget } = useSessionResolver()

  if (isChecking) {
    return null
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTarget} replace />
  }

  return <LoginPage />
}

function CatchAllRoute() {
  const { isChecking, isAuthenticated, redirectTarget } = useSessionResolver()

  if (isChecking) {
    return null
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTarget} replace />
  }

  return <Navigate to="/login" replace />
}

export const router = createBrowserRouter([
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // Public routes — redirect authenticated users
  {
    path: '/login',
    element: <PublicLoginRoute />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/register/workspace',
    element: <RegisterWorkspacePage />,
  },
  {
    path: '/accept-invite',
    element: <AcceptInvitePage />,
  },
  // Public form submission — no auth required
  {
    path: '/forms/public/:token',
    element: <PublicFormPage />,
  },

  // Auth layout with workspace selection
  {
    element: <AuthLayout />,
    children: [
      {
        path: '/workspace-select',
        element: <WorkspaceSelectPage />,
      },
    ],
  },

  // Protected routes with AppShell
  {
    element: <AuthLayout />,
    children: [
      {
        element: <RouteErrorBoundary><AppShell /></RouteErrorBoundary>,
        children: [
          {
            index: true,
            element: <Navigate to="/record-groups" replace />,
          },
          { path: '/dashboard/:dashboardId?',   element: <DashboardPage /> },

          // Record Groups
          { path: '/record-groups',                                element: <RecordGroupsPage /> },
          { path: '/record-groups/new',                            element: <NewRecordGroupPage /> },
          {
            path: '/record-groups/:recordGroupId',
            element: <RecordGroupRoute />,
            children: [
              { index: true, element: <RecordGroupPage /> },
              { path: 'form', element: <FormDesigner /> },
            ],
          },
          { path: '/record-groups/:recordGroupId/records/:recordId', element: <RecordDetailPage /> },

          // Automations
          { path: '/automations',              element: <RequirePermission permission={PERMISSION_KEYS.AUTOMATION_VIEW} anywhere><AutomationsPage /></RequirePermission> },
          { path: '/automations/:automationId', element: <RequirePermission permission={PERMISSION_KEYS.AUTOMATION_VIEW} anywhere><AutomationDetailPage /></RequirePermission> },

          // Roles
          { path: '/roles',          element: <Navigate to="/settings?tab=roles" replace /> },
          { path: '/roles/:roleId',  element: <RequirePermission permission={PERMISSION_KEYS.ROLE_VIEW} anywhere><RoleDetailPage /></RequirePermission> },

          // Users  ← replaces the old /permissions route
          { path: '/users',           element: <Navigate to="/settings?tab=users" replace /> },
          { path: '/users/:userId',   element: <RequirePermission permission={PERMISSION_KEYS.USER_VIEW}><UserDetailPage /></RequirePermission> },

          // Keep /permissions redirect for any old bookmarks
          { path: '/permissions',     element: <Navigate to="/settings?tab=users" replace /> },

          // Workspace settings: users, roles, billing and trash live in its tabs (tabs check their own permissions)
          { path: '/settings',        element: <WorkspaceSettingsPage /> },
          { path: '/subscription',    element: <Navigate to="/settings?tab=billing" replace /> },
          { path: '/trash',           element: <Navigate to="/settings?tab=trash" replace /> },

          // Calling
          { path: '/calls',               element: <RequireCalling><CallLogPage /></RequireCalling> },
          { path: '/calling/my-phone',    element: <RequireCalling><RequirePermission permission={PERMISSION_KEYS.CALL_MAKE}><MyPhonePage /></RequirePermission></RequireCalling> },
          { path: '/calling/settings',    element: <RequireCalling><RequirePermission permission={PERMISSION_KEYS.CALL_CONFIGURE}><TelephonySettingsPage /></RequirePermission></RequireCalling> },

          { path: '/account',        element: <AccountPage /> },

          // Mailboxes are a settings tab; the OAuth redirect lands on its own route
          { path: '/settings/mailboxes',          element: <Navigate to="/settings?tab=mailboxes" replace /> },
          { path: '/settings/mailboxes/callback', element: <MailboxCallbackPage /> },

          // Task 17: notifications page
          { path: '/notifications',   element: <NotificationsPage /> },
          // Tasks
          { path: '/tasks',           element: <TasksPage /> },
        ],
      },
    ],
  },

  // E-mailed owner links: signed in, but outside the app shell
  {
    element: <AuthLayout />,
    children: [{ path: '/confirm', element: <ConfirmPage /> }],
  },

  // Developer routes
  {
    element: <AuthLayout />,
    children: [
      {
        element: <RouteErrorBoundary><DeveloperLayout /></RouteErrorBoundary>,
        children: [
          { path: '/developer',                             element: <DeveloperDashboardPage /> },
          { path: '/developer/workspaces',                 element: <DeveloperWorkspacesPage /> },
          { path: '/developer/workspaces/:workspaceId',    element: <WorkspaceDetailPage /> },
          { path: '/developer/workspaces/:workspaceId/subscription', element: <WorkspaceSubscriptionPage /> },
          { path: '/developer/users',                      element: <DeveloperUsersPage /> },
          { path: '/developer/analytics',                  element: <DeveloperAnalyticsPage /> },
        ],
      },
    ],
  },

  // Catch all — auth-aware redirect
  {
    path: '*',
    element: <CatchAllRoute />,
  },
])

